/**
 * Quotas. On a self-hosted Enterprise AIOE there is no billing, so the administrators set how much
 * the organisation may keep: one default for the whole organisation, and a figure of their own for a
 * person (backups, exports). Memory counts toward total storage and has no limit of its own. Nothing is limited until a limit is set. Each limit
 * shows what is used of it beside it (the hourly storage count); a write past a limit is refused in
 * words that name the limit and that an administrator can raise it here. Below, Storage and limits
 * shows every store against its limit and the retention that bounds it.
 *
 * On Mojo Up AI Cloud the plan's limits apply and are on Billing, so this page sends people there.
 *
 * Laid out as settings are (Layout.tsx): the form column, one list group per kind of limit with a
 * number box at the end of each row, a sheet to add a person, and one floating Save.
 */

import { QUOTA_POLICY_KEYS, QUOTA_POLICY_TEXT, QUOTA_SUBJECT_KEYS, quotaAmountText, type QuotaLimits, type QuotaOverride, type QuotaPolicy, type QuotaPolicyKey, type QuotaPolicyView } from '@aioe/shared';
import { ChartPie, Database, Persons, Sliders } from '@gravity-ui/icons';
import { useState } from 'react';
import { Navigate } from 'react-router-dom';

import { problemText, routes } from '../api/client.ts';
import { Button, ChoiceSelect, Empty, ErrorNote, inputClass, ListGroup, ListRow, PageColumn, PageHeader, SaveBar, Stat, StatusMark } from '../components/ui.tsx';
import { SubjectPicker } from '../components/SubjectPicker.tsx';
import { StoragePanel } from '../components/storage.tsx';
import { VIEW_TONES } from '../components/ui/Art.tsx';
import { FormField, FormSheet } from '../components/ui/FormSheet.tsx';
import { Explainer } from '../components/ui/Layout.tsx';
import { RowMenu } from '../components/ui/RowMenu.tsx';
import { isCloud } from '../edition.ts';
import { useLoad } from '../hooks.ts';
import { PartBoundary } from '../components/ui/PartBoundary.tsx';

const tone = VIEW_TONES.quotas!;
const MiB = 1024 * 1024;
const GiB = 1024 * MiB;

type Draft = { defaults: QuotaLimits; overrides: QuotaOverride[] };
const clean = (l: QuotaLimits): QuotaLimits => Object.fromEntries(Object.entries(l).filter(([, v]) => v !== undefined)) as QuotaLimits;
const asPolicy = (d: Draft): QuotaPolicy => ({ defaults: clean(d.defaults), overrides: d.overrides.map((o) => ({ ...o, limits: clean(o.limits) })).filter((o) => Object.keys(o.limits).length > 0) });
const same = (a: Draft, b: Draft) => JSON.stringify(asPolicy(a)) === JSON.stringify(asPolicy(b));

/**
 * A limit as a number box (and MB or GB for bytes). Empty means no limit. It keeps what is being typed
 * itself and tells its owner a whole number (or nothing) each time it makes sense.
 */
function LimitField({ label, unit, value, onChange }: { label: string; unit: 'bytes' | 'count'; value: number | null | undefined; onChange: (v: number | undefined) => void }) {
  const [gb, setGb] = useState(typeof value === 'number' && value >= GiB);
  const scale = unit === 'bytes' ? (gb ? GiB : MiB) : 1;
  const [text, setText] = useState(typeof value === 'number' ? String(+(value / (unit === 'bytes' ? (value >= GiB ? GiB : MiB) : 1)).toFixed(2)) : '');
  const push = (t: string, s: number) => {
    const n = Number(t);
    onChange(t.trim() === '' || !Number.isFinite(n) || n < 0 ? undefined : Math.round(n * s));
  };
  return (
    <span className="flex items-center gap-2">
      <input
        aria-label={`${label}: limit`}
        inputMode="decimal"
        className={`${inputClass} w-28 text-right tabular-nums`}
        placeholder="No limit"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          push(e.target.value, scale);
        }}
      />
      {unit === 'bytes' ? (
        <ChoiceSelect<'MB' | 'GB'>
          label={`${label}: unit`}
          className="w-20"
          options={{ MB: 'MB', GB: 'GB' }}
          value={gb ? 'GB' : 'MB'}
          onChange={(u) => {
            setGb(u === 'GB');
            push(text, u === 'GB' ? GiB : MiB);
          }}
        />
      ) : (
        <span className="w-20 text-sm text-muted">items</span>
      )}
    </span>
  );
}

const amount = (key: QuotaPolicyKey, n: number) => quotaAmountText(QUOTA_POLICY_TEXT[key].unit, n);

/** What is used of one limit, in words beside it. */
function usedText(key: QuotaPolicyKey, used: number, limit: number | null | undefined): string {
  return limit === null || limit === undefined ? `${amount(key, used)} used` : `${amount(key, used)} of ${amount(key, limit)}`;
}

function mark(used: number | undefined, limit: number | null | undefined) {
  if (used === undefined || limit === null || limit === undefined) return undefined;
  if (used >= limit) return <StatusMark state="error" label="At the limit" />;
  if (used >= limit * 0.8) return <StatusMark state="medium" label="Nearly full" />;
  return undefined;
}

export function QuotasPage() {
  if (isCloud()) return <Navigate to="/billing" replace />;
  return <EnterpriseQuotas />;
}

function EnterpriseQuotas() {
  const policy = useLoad(() => routes.quotaPolicy(), []);
  if (!policy.data)
    return (
      <PageColumn width="form">
        <PageHeader tone={tone} icon={<Sliders />} title="Quotas" subtitle="How much the organisation or a person may keep." />
        {policy.error ? <ErrorNote error={policy.error} /> : <p className="text-sm text-muted">Reading the limits…</p>}
      </PageColumn>
    );
  // A saved policy has a new updatedAt: the editor starts again from what the platform now holds.
  return <Editor key={policy.data.updatedAt ?? 'none'} v={policy.data} onSaved={policy.reload} />;
}

function Editor({ v, onSaved }: { v: QuotaPolicyView; onSaved: () => void }) {
  const start = (): Draft => ({ defaults: structuredClone(v.defaults), overrides: structuredClone(v.overrides) });
  const [draft, setDraft] = useState<Draft>(start);
  const [saved] = useState<Draft>(start);
  const [version, setVersion] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [adding, setAdding] = useState(false);

  const dirty = !same(draft, saved);
  const set = (key: QuotaPolicyKey, n: number | undefined) => setDraft((d) => ({ ...d, defaults: { ...d.defaults, [key]: n } }));
  const setOverride = (i: number, key: QuotaPolicyKey, n: number | undefined) => setDraft((d) => ({ ...d, overrides: d.overrides.map((o, j) => (j === i ? { ...o, limits: { ...o.limits, [key]: n } } : o)) }));
  const addOverride = (o: QuotaOverride) => {
    setAdding(false);
    setDraft((d) => (!d.overrides.some((x) => x.subjectType === o.subjectType && x.subjectId === o.subjectId) ? { ...d, overrides: [...d.overrides, o] } : d));
    setVersion((n) => n + 1);
  };
  const save = async () => {
    setSaving(true);
    setError(undefined);
    try {
      await routes.saveQuotaPolicy(asPolicy(draft));
      onSaved();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setSaving(false);
    }
  };

  const setCount = Object.values(clean(draft.defaults)).filter((x) => x !== undefined && x !== null).length;
  const used = v.used;
  const usedOf: Record<QuotaPolicyKey, number | undefined> = {
    storageBytes: used.storageBytes,
    backupBytes: used.backupBytes,
    assetBytes: used.assetBytes,
    meshNodes: used.meshNodes,
    exportBytes: used.largestExportBytes,
  };
  const usedWhat: Partial<Record<QuotaPolicyKey, string>> = { backupBytes: 'largest person', exportBytes: 'largest export' };
  const total = draft.defaults.storageBytes;
  const savedOverride = (o: QuotaOverride) => v.overrides.find((x) => x.subjectType === o.subjectType && x.subjectId === o.subjectId);

  return (
    <PageColumn width="form">
      <PageHeader tone={tone} icon={<Sliders />} title="Quotas" subtitle="How much the organisation or a person may keep." />
      <Explainer id="quotas" tone={tone} icon={<Sliders />} title="How quotas work" summary="Nothing is limited until you set a limit here">
        <p>Each limit is a ceiling on what the organisation keeps: storage in all, each person’s backups and exports, the design systems’ files and machines on the mesh. Memory counts toward total storage, so it has no limit of its own. Leave a box empty and that thing has no limit.</p>
        <p>A person or a project can have a figure of their own, which replaces the organisation’s for them: a person’s backups and exports.</p>
        <p>Work that would pass a limit is refused in words that name the limit and say an administrator can raise it here. What is used is counted every hour.</p>
      </Explainer>
      <PartBoundary key="quotas" name="Quotas" tone={tone}>
        <div className="art-stats">
          <Stat tone={tone} icon={<ChartPie />} label="Storage" value={quotaAmountText('bytes', used.storageBytes)} detail={total === undefined || total === null ? 'No limit set' : `of ${quotaAmountText('bytes', total)}`} needsYou={typeof total === 'number' && used.storageBytes >= total * 0.8} />
          <Stat tone={tone} icon={<Database />} label="Memory" value={quotaAmountText('bytes', used.memoryBytes)} detail="Counts toward total storage" />
          <Stat tone={tone} icon={<Persons />} label="Limits set" value={setCount + draft.overrides.length} detail={`${draft.overrides.length} for a person`} />
        </div>
        <ErrorNote error={error} />
        <ListGroup title="For the whole organisation" description="Leave a box empty for no limit. Backups and exports are counted for each person.">
          {QUOTA_POLICY_KEYS.map((key) => {
            const t = QUOTA_POLICY_TEXT[key];
            const n = usedOf[key];
            return (
              <ListRow
                key={`${key}/${version}`}
                title={t.label}
                description={
                  <>
                    <span className="block">{t.what}</span>
                    {n !== undefined ? (
                      <span className="block tabular-nums">
                        {usedText(key, n, saved.defaults[key])}
                        {usedWhat[key] ? ` (${usedWhat[key]})` : ''}
                      </span>
                    ) : null}
                  </>
                }
                status={mark(n, saved.defaults[key])}
                control={<LimitField label={t.label} unit={t.unit} value={draft.defaults[key]} onChange={(x) => set(key, x)} />}
              />
            );
          })}
        </ListGroup>
        <ListGroup
          title="For a person"
          count={draft.overrides.length || undefined}
          description="A figure of their own replaces the organisation’s: a person’s backups and exports."
          actions={
            draft.overrides.length ? (
              <Button size="sm" variant="secondary" onPress={() => setAdding(true)}>
                Add a person
              </Button>
            ) : undefined
          }
          empty={
            draft.overrides.length === 0 ? (
              <Empty
                tone={tone}
                icon={<Persons />}
                title="Everyone follows the organisation’s limits"
                body="Give a person more room for backups and exports without raising the limit for everyone."
                action={
                  <Button size="sm" variant="secondary" onPress={() => setAdding(true)}>
                    Add a person
                  </Button>
                }
              />
            ) : undefined
          }
        >
          {draft.overrides.map((o, i) => {
            const keys: readonly QuotaPolicyKey[] = QUOTA_SUBJECT_KEYS[o.subjectType];
            const own = v.overrideUsed.find((x) => x.subjectType === o.subjectType && x.subjectId === o.subjectId)?.used ?? {};
            const name = o.name ?? o.subjectId;
            return (
              <ListRow
                key={`${o.subjectType}:${o.subjectId}/${version}`}
                title={name}
                description="A person: their own backups and exports"
                compactControl
                control={<RowMenu label={`More for ${name}`} items={[{ id: 'remove', label: 'Remove their figures', danger: true }]} onAction={() => setDraft((d) => ({ ...d, overrides: d.overrides.filter((_, j) => j !== i) }))} />}
              >
                <div className="grid gap-3 pt-1">
                  {keys.map((key) => {
                    const t = QUOTA_POLICY_TEXT[key];
                    const n = own[key];
                    const limit = savedOverride(o)?.limits[key];
                    return (
                      <div key={key} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                        <span className="min-w-0 flex-1 basis-48">
                          <span className="block text-sm">{t.label}</span>
                          {n !== undefined ? (
                            <span className="flex flex-wrap items-center gap-x-3 text-xs tabular-nums text-muted">
                              <span>{usedText(key, n, limit)}</span>
                              {mark(n, limit)}
                            </span>
                          ) : null}
                        </span>
                        <LimitField label={`${name} ${t.label}`} unit={t.unit} value={o.limits[key]} onChange={(x) => setOverride(i, key, x)} />
                      </div>
                    );
                  })}
                </div>
              </ListRow>
            );
          })}
        </ListGroup>
        <StoragePanel tone={tone} />
        <SaveBar
          isOpen={dirty}
          label="Unsaved limits"
          saveLabel="Save limits"
          saving={saving}
          onSave={() => void save()}
          onDiscard={() => {
            setDraft(structuredClone(saved));
            setVersion((n) => n + 1);
          }}
        />
      </PartBoundary>
      <AddSubject isOpen={adding} onClose={() => setAdding(false)} onAdd={addOverride} />
    </PageColumn>
  );
}

/** The sheet that adds a person to the list; their figures are then set on the row and saved with the rest. */
function AddSubject({ isOpen, onClose, onAdd }: { isOpen: boolean; onClose: () => void; onAdd: (o: QuotaOverride) => void }) {
  return (
    <FormSheet
      isOpen={isOpen}
      width="md"
      title="Add a person"
      description="They get a row of their own; set their figures there and save the limits."
      onClose={onClose}
      footer={
        <Button size="sm" variant="tertiary" onPress={onClose}>
          Cancel
        </Button>
      }
    >
      <FormField label="Person" hint="Their backups and exports get a limit of their own.">
        <SubjectPicker kinds={['user']} placeholder="Find a person by name" onPick={(s) => onAdd({ subjectType: 'user', subjectId: s.id, name: s.name, limits: {} })} />
      </FormField>
    </FormSheet>
  );
}
